import type {PromptAttachment, UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PendingUploadStatus, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getUniqueId} from '@Utils/string.util';

interface PendingUploadEntry {
  promise: Promise<unknown>;
  blobUrl: string;
  file: File;
}

const pendingUploadRegistry = new Map<string, Map<string, PendingUploadEntry>>();

export const generatePendingLocalId = (): string => {
  return `pending-${getUniqueId(8)}`;
};

export const registerPendingUpload = (promptBoxId: string, localId: string, blobUrl: string, promise: Promise<unknown>, file: File): void => {
  let entries = pendingUploadRegistry.get(promptBoxId);
  if (!entries) {
    entries = new Map();
    pendingUploadRegistry.set(promptBoxId, entries);
  }
  entries.set(localId, {promise, blobUrl, file});
};

export const updatePendingUploadPromise = (promptBoxId: string, localId: string, promise: Promise<unknown>): void => {
  const entry = pendingUploadRegistry.get(promptBoxId)?.get(localId);
  if (!entry) {
    return;
  }
  entry.promise = promise;
};

export const getPendingUploadFile = (promptBoxId: string, localId: string): File | undefined => {
  return pendingUploadRegistry.get(promptBoxId)?.get(localId)?.file;
};

export const clearPendingUpload = (promptBoxId: string, localId: string): void => {
  const entries = pendingUploadRegistry.get(promptBoxId);
  const entry = entries?.get(localId);
  if (!entries || !entry) {
    return;
  }
  URL.revokeObjectURL(entry.blobUrl);
  entries.delete(localId);
  if (entries.size === 0) {
    pendingUploadRegistry.delete(promptBoxId);
  }
};

export const awaitPendingUploadsForPromptBox = async (promptBoxId: string): Promise<void> => {
  const entries = pendingUploadRegistry.get(promptBoxId);
  if (!entries || entries.size === 0) {
    return;
  }
  const promises = Array.from(entries.values()).map((entry) => entry.promise);
  await Promise.allSettled(promises);
};

export const isPendingImageAttachment = (attachment: PromptAttachment): attachment is UserImageAttachment & {localId: string} => {
  if (attachment.type !== PromptAttachmentType.IMAGE && attachment.type !== PromptAttachmentType.CAPTURE_PHOTO) {
    return false;
  }
  return attachment.localId !== undefined && attachment.pendingUpload !== undefined;
};

export const isPendingUploadInFlight = (attachment: PromptAttachment): boolean => {
  return isPendingImageAttachment(attachment) && attachment.pendingUpload?.status === PendingUploadStatus.UPLOADING;
};

export const isPendingUploadFailed = (attachment: PromptAttachment): boolean => {
  return isPendingImageAttachment(attachment) && attachment.pendingUpload?.status === PendingUploadStatus.FAILED;
};

export const getPendingImageAttachments = (items: PromptAttachment[] | undefined): (UserImageAttachment & {localId: string})[] => {
  return (items ?? []).filter(isPendingImageAttachment);
};

export const getInFlightPendingAttachments = (items: PromptAttachment[] | undefined): (UserImageAttachment & {localId: string})[] => {
  return getPendingImageAttachments(items).filter((item) => item.pendingUpload?.status === PendingUploadStatus.UPLOADING);
};
